import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import "react-tooltip/dist/react-tooltip.css";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchAllergy,
  updateAllergy,
  createAllergy,
} from "../../../../../redux/modules/patient/actions";
import { displayToast } from "../../../../../redux/modules/toast/actions";
import { useParams } from "react-router-dom";
import Medications from "../../../clinical-services/medications/medications";
import SubNavigation from "../../../clinical-services/common/sub-navigation";
import ChartNav from "../../../../common/ChartNav";
import AllergyCreate from "./AllergyCreate";
import Button from "../../../../common/Button";
import InterventionCreate from "../../../clinical-services/service-steps/medication/interventionCreate";
import AllergyInformationHeader from "./allergy-information-header";
import AllergyTable from "./table";
import NewToolTip from "../../../../common/New-ToolTip";

const PatientAllergies = ({
  confirmedAllergy = [],
  clinicalServiceName = null,
  updateConfirmedAllergy = () => {},
  updateValidation = () => {},
  allergiesProcessed = false,
  clinicalServiceObj = null
}) => {
  const dispatch = useDispatch();
  const { id: patientId } = useParams();
  const { allergy } = useSelector((state) => state.patients);

  const [showCreateSlider, setShowCreateSlider] = useState(false);
  const [confirmNoAllergy, setConfirmNoAllergy] = useState(false);
  
  const [selectedAllergy, setSelectedAllergy] = useState(null);
  const [create, setCreate] = useState(true);

  const filterOptions = [
    { value: "all", label: "All" },
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];

  const [isActiveAllergyPresent, setIsActiveAllergyPresent] = useState(null);
  const [allAllergies, setAllAllergies] = useState([]);
  const [showInterventionDialog, setShowInterventionDialog] = useState(false);
  const [tableFilter, setTableFilter] = useState("active");
  
  const [wasClinicalServiceConfirmed, setWasClinicalServiceConfirmed] = useState(() => {
    return sessionStorage.getItem('wasClinicalServiceConfirmed') === 'true';
  });

  useEffect(() => {
    if (clinicalServiceName) {
      const storedConfirmedAllergies = clinicalServiceObj?.initialAssessment?.initialAssessmentForm?.clinicalAssessment?.confirmedAllergy ?? [];
      const hasStoredConfirmations = storedConfirmedAllergies.length > 0 || 
        clinicalServiceObj?.initialAssessment?.initialAssessmentForm?.clinicalAssessment?.allergy === true;
      
      if (!hasStoredConfirmations) {
        setWasClinicalServiceConfirmed(false);
        sessionStorage.removeItem('wasClinicalServiceConfirmed');
      }
    }
  }, [clinicalServiceName, clinicalServiceObj]);

  useEffect(() => {
    if (!clinicalServiceName) {
      setConfirmNoAllergy(false);
      return;
    }

    const storedConfirmedAllergies = clinicalServiceObj?.initialAssessment?.initialAssessmentForm?.clinicalAssessment?.confirmedAllergy ?? [];
    const wasNoAllergyConfirmed = clinicalServiceObj?.initialAssessment?.initialAssessmentForm?.clinicalAssessment?.allergy === true && storedConfirmedAllergies.length === 0;
    const hasActiveAllergies = allergy?.allergies?.some(a => a.status === "active");
    
    if (wasClinicalServiceConfirmed && hasActiveAllergies && storedConfirmedAllergies.length > 0) {
      const activeAllergyIds = allergy?.allergies?.filter(a => a.status === "active").map(a => a.id) || [];
      const newAllergies = activeAllergyIds.filter(id => !storedConfirmedAllergies.includes(id));
      
      if (newAllergies.length > 0) {
        const updatedConfirmedAllergies = [...storedConfirmedAllergies, ...newAllergies];
        updateConfirmedAllergy(updatedConfirmedAllergies);
        setConfirmNoAllergy(false);
        const allConfirmed = activeAllergyIds.every(id => updatedConfirmedAllergies.includes(id));
        updateValidation(allConfirmed);
      } else {
        updateConfirmedAllergy(storedConfirmedAllergies);
        setConfirmNoAllergy(false);
        const activeAllergies = allergy.allergies.filter(a => a.status === "active");
        const allConfirmed = activeAllergies.every(a => storedConfirmedAllergies.includes(a.id));
        updateValidation(allConfirmed);
      }
    } else if (wasClinicalServiceConfirmed && !hasActiveAllergies && wasNoAllergyConfirmed) {
      setConfirmNoAllergy(true);
      updateConfirmedAllergy([]);
      updateValidation(true);
    }
  }, [clinicalServiceName, wasClinicalServiceConfirmed, allergy?.allergies]);

  useEffect(() => {
    dispatch(fetchAllergy(patientId));
  }, []);

  const activeAllergyPresentCheck = useCallback(() => {
    if (allergy?.noKnownAllergies === true) return false;
    return allergy?.allergies?.length > 0 && allergy.allergies?.filter((val) => val.status === "active")?.length > 0;
  }, [allergy?.noKnownAllergies, allergy?.allergies]);

  const isAllergyConfirmed = useCallback((allergyId) => {
    return confirmedAllergy.includes(allergyId);
  }, [confirmedAllergy]);

  const checkAllAllergyConfirmed = () => {
    const activeAllergies = allAllergies.filter(allergy => allergy.status === "active");
    return activeAllergies.length > 0 && activeAllergies.every(allergy => confirmedAllergy.includes(allergy.id));
  };

  const validateAllergyConfirmation = () => {
    const hasActiveAllergies = activeAllergyPresentCheck();
    const allActiveConfirmed = hasActiveAllergies && checkAllAllergyConfirmed();
    updateValidation(confirmNoAllergy || allActiveConfirmed);
  };

  useEffect(() => {
    if (!allergy) return;
    
    if (allergy.allergies) {
      const sortedAllergies = allergy.allergies.sort((a, b) =>
        a.allergyName.localeCompare(b.allergyName)
      );
      setAllAllergies(sortedAllergies);
    }
    
    const shouldBeActive = activeAllergyPresentCheck();
    setIsActiveAllergyPresent(shouldBeActive);
  }, [allergy?.allergies?.length, activeAllergyPresentCheck]);

  useEffect(() => {
    validateAllergyConfirmation();
  }, [confirmNoAllergy, confirmedAllergy, allAllergies]);

  const isInClinicalService = window.location.pathname.includes('/clinical-services/');
  const hasAnyConfirmation = confirmedAllergy.length > 0 || confirmNoAllergy;

  useEffect(() => {
    if (!isInClinicalService) return;

    if (hasAnyConfirmation) {
      setWasClinicalServiceConfirmed(true);
      sessionStorage.setItem('wasClinicalServiceConfirmed', 'true');
    }
  }, [hasAnyConfirmation, isInClinicalService]);

  useEffect(() => {
    if (!clinicalServiceName || !wasClinicalServiceConfirmed) return;

    const applyUpdate = (allergy) => {
      if (!allergy?.id) return;

      updateConfirmedAllergy((prev) => {
        const isConfirmed = prev.includes(allergy.id);
        if (allergy.status === "active" && !isConfirmed) {
          return [...prev, allergy.id];
        }
        if (allergy.status === "inactive" && isConfirmed) {
          return prev.filter((id) => id !== allergy.id);
        }
        return prev;
      });
    };

    const handleAllergyAdded = (event) => applyUpdate(event.detail.allergy);
    const handleAllergyUpdated = (event) => applyUpdate(event.detail.allergy);

    window.addEventListener('allergyAdded', handleAllergyAdded);
    window.addEventListener('allergyUpdated', handleAllergyUpdated);

    return () => {
      window.removeEventListener('allergyAdded', handleAllergyAdded);
      window.removeEventListener('allergyUpdated', handleAllergyUpdated);
    };
  }, [clinicalServiceName, wasClinicalServiceConfirmed, updateConfirmedAllergy]);

  const manageClick = () => {
    document.body.classList.toggle("overflow-hidden");
  };

  const confirmAllergies = () => {
    const filteredAllergies = allAllergies.filter(
      (allergy) => allergy.status === "active"
    );
    filteredAllergies.forEach((item) => {
      const completeAllergyObject = {
        ...item,
        confirmedDate: new Date(),
      };
      const { id, ...updatedObject } = completeAllergyObject;
      dispatch(updateAllergy(id, updatedObject, clinicalServiceName !== null));
    });
    setAllAllergies(
      filteredAllergies.map((x) => ({
        ...x,
        confirmedDate: new Date(),
        confirmed: true,
      }))
    );
    updateConfirmedAllergy(filteredAllergies.map((val) => val.id));
  };

  const handleChange = (e, allergyID) => {
    const isChecked = e.target.checked;
    const allergyObjectToUpdate = allAllergies.find(
      (allergy) => allergy.id === allergyID
    );
    allergyObjectToUpdate.confirmedDate = isChecked ? new Date() : null;
    const { id, ...updatedObject } = allergyObjectToUpdate;
    dispatch(updateAllergy(id, updatedObject, clinicalServiceName !== null, true)); // Pass fromClinicalService parameter
    if (isChecked) {
      if (!confirmedAllergy.includes(id)) {
        updateConfirmedAllergy([...confirmedAllergy, id]);
      }
    } else {
      updateConfirmedAllergy(confirmedAllergy.filter((val) => val !== id));
    }
    setAllAllergies((prev) =>
      prev.map((allergy) =>
        allergy.id === allergyID ? allergyObjectToUpdate : allergy
      )
    );
  };

  const activeAllergyCount = allAllergies.filter(
    (allergy) => allergy.status === "active"
  ).length;
  const disableNoKnownAllergy = activeAllergyCount > 0;
  const noKnownAllergyTooltipId = "no-known-allergy-disabled-tooltip";

  const getBorderClass = (isActive) =>
    isActive ? "border-2 border-gray-100" : "border border-gray-100";

  return (
    <div className="relative flex flex-col flex-1 w-full gap-4 bg-white">
      <div className="flex flex-col flex-1">
        <div className="flex flex-1 gap-4">
          {clinicalServiceName !== null && (
            <>
              <ChartNav />
              <Medications />
            </>
          )}
          <div className="flex flex-1 h-full">
            <div className="w-full gap-5 h-full">
              <div className="flex items-center justify-between mt-0.5 mb-0.5">
                <h4 className="text-xs-display font-bold text-teal-300">
                  Allergies
                </h4>
                {clinicalServiceName !== null && <SubNavigation />}
              </div>
              <div className="border-b-[7px] border-gray-20 mb-[20px]"></div>
              <div className="flex flex-col h-[590px] gap-6 overflow-scroll overflow-y-auto overflow-x-hidden">
                <div
                  className={`flex justify-between p-6 rounded ${
                    isActiveAllergyPresent === false ? "bg-gray-20" : ""
                  } ${getBorderClass(isActiveAllergyPresent === false)}`}
                >
                  <div
                    className="flex items-center"
                    data-tooltip-id={
                      disableNoKnownAllergy ? noKnownAllergyTooltipId : undefined
                    }
                    data-tooltip-place="bottom"
                  >
                    <input
                      id="no-known-allergy"
                      name="no-known-allergy"
                      onChange={() => {
                        setIsActiveAllergyPresent(false);
                        setConfirmNoAllergy(false); // Reset confirmation when switching
                        updateConfirmedAllergy([]);
                      }}
                      checked={isActiveAllergyPresent === false}
                      type="radio"
                      disabled={disableNoKnownAllergy}
                      className={`w-[45px] h-[45px] border-gray-100 form-radio text-REELTeal focus:ring-transparent ${
                        disableNoKnownAllergy
                          ? "cursor-not-allowed opacity-60"
                          : "cursor-pointer"
                      }`}
                    />
                    <div className="flex flex-col pl-6">
                      <h3
                        className={`text-xl font-semibold ${
                          disableNoKnownAllergy ? "text-gray-600" : "text-gray-900"
                        }`}
                      >
                        No Known Allergy
                      </h3>
                      <p
                        className={`text-body-md ${
                          disableNoKnownAllergy ? "text-gray-600" : "text-gray-800"
                        }`}
                      >
                        Drug, Food, Environmental, Other
                      </p>
                    </div>
                  </div>

                  {clinicalServiceName !== null &&
                    isActiveAllergyPresent === false && (
                      <Button
                        className={`text-body-md rounded-[4px] !w-[200px] !h-full ${
                          confirmNoAllergy ? "!py-[14px] !px-[32px]" : ""
                        }`}
                        type={confirmNoAllergy ? "primary" : "primary-outline"}
                        text={
                          confirmNoAllergy ? "Confirmed" : "Click to Confirm"
                        }
                        onClick={() => {
                          if (!confirmNoAllergy) {
                            setConfirmNoAllergy(true);
                            updateConfirmedAllergy([]);
                          }
                        }}
                      />
                    )}
                </div>

                <div
                  className={`flex flex-col gap-6 p-6 rounded ${
                    isActiveAllergyPresent === true ? "bg-gray-20" : ""
                  } ${getBorderClass(isActiveAllergyPresent === true)}`}
                >
                  <AllergyInformationHeader
                    setIsActiveAllergyPresent={setIsActiveAllergyPresent}
                    setConfirmNoAllergy={setConfirmNoAllergy}
                    allAllergies={allAllergies}
                    setShowCreateSlider={setShowCreateSlider}
                    isActiveAllergyPresent={isActiveAllergyPresent}
                    filterOptions={filterOptions}
                    tableFilter={tableFilter}
                    setTableFilter={setTableFilter}
                    allergies={allAllergies}
                  />
                  {allAllergies.length > 0 && isActiveAllergyPresent && (
                    <AllergyTable
                      label={"Allergy Record"}
                      tableFilter={tableFilter}
                      allergies={allAllergies}
                      allAllergies={allAllergies}
                      isAllergyConfirmed={isAllergyConfirmed}
                      handleChange={handleChange}
                      checkAllAllergyConfirmed={checkAllAllergyConfirmed}
                      confirmAllergies={confirmAllergies}
                      setShowCreateSlider={setShowCreateSlider}
                      setSelectedAllergy={setSelectedAllergy}
                      setCreate={setCreate}
                      showConfirmedAllergy={clinicalServiceName !== null}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AllergyCreate
        open={showCreateSlider}
        onClose={() => {
          setShowCreateSlider(false);
          manageClick();
          setSelectedAllergy(null);
          setCreate(true);
        }}
        selectedAllergy={selectedAllergy}
        patientId={patientId}
        create={create}
        onSave={async () => {
          if (patientId) {
            await dispatch(fetchAllergy(patientId));
          }
        }}
      />
      <InterventionCreate
        open={showInterventionDialog}
        onClose={(value) => setShowInterventionDialog(false)}
      />

      {disableNoKnownAllergy && (
        <NewToolTip
          id={noKnownAllergyTooltipId}
          render={() => (
            <div className="bg-gray-20 p-2 max-w-md flex flex-col">
              <p className="text-teal-300 text-left text-body-sm font-normal">
                Delete all allergies to enable 'No Known Allergies'.
              </p>
            </div>
          )}
        />
      )}
    </div>
  );
};

export default PatientAllergies;